import authApi from '@api/authApi';
import { ActionButton } from '@components/actionButton';
import { LinkButton } from '@components/linkComponents';
import LoginHeader from '@components/login/loginHeader';
import NoAccount from '@components/login/noAccount';
import useLocalizeError from '@hooks/useLocalizeError';
import { useTranslator } from '@i18n';
import { Button, Card, Group, PasswordInput, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconLock, IconUser } from '@tabler/icons-react';
import { handleError } from '@util/errors';
import { createForm } from 'cross-state/react';

const { Form, Field, useForm } = createForm({
  defaultValue: {
    user: '',
    password: '',
  },
  validations: {
    user: { required: (x) => !!x },
    password: { required: (x) => !!x },
  },
});

export default function LoginPage() {
  const localizeError = useLocalizeError();

  return (
    <Form
      css={{
        flex: 1,
        padding: 'var(--mantine-spacing-md)',
        display: 'grid',
        gridTemplateRows: '1fr max-content 1fr',
        justifyContent: 'center',
      }}
      localizeError={localizeError}
    >
      <LoginPageContent />
    </Form>
  );
}

function LoginPageContent() {
  const t = useTranslator();
  const form = useForm();

  async function login() {
    if (!form.validate()) {
      return;
    }

    const { user, password } = form.getDraft();

    await handleError(() => authApi.login(user, password), {
      title: t('login.loginFailed'),
      knownErrors: [[{ 'response.status': 401 }, 'login.wrongEmailOrPassword']],
    });
  }

  return (
    <>
      <LoginHeader />

      <Card
        withBorder
        shadow="md"
        css={{
          display: 'grid',
          gap: '1rem',

          '.validated & input:invalid': {
            borderColor: 'var(--mantine-color-red-6)',
          },
        }}
      >
        <h2>{t('login.title')}</h2>

        <Field
          name="user"
          render={(props) => (
            <TextInput
              {...props}
              autoComplete="username"
              spellCheck={false}
              leftSection={<IconUser />}
              label={t('login.user')}
              placeholder="user@example.com"
            />
          )}
        />

        <Field
          name="password"
          render={(props) => (
            <PasswordInput
              {...props}
              type="password"
              autoComplete="current-password"
              leftSection={<IconLock />}
              label={t('login.password')}
            />
          )}
        />

        <ActionButton type="submit" onClick={login}>
          {t('login.login')}
        </ActionButton>

        <Group justify="center">
          <Button
            variant="transparent"
            onClick={() => {
              modals.open({
                centered: true,
                title: t('login.noAccount'),
                children: <NoAccount />,
              });
            }}
          >
            {t('login.noAccount')}
          </Button>

          <LinkButton variant="transparent" to="/forgot-password">
            {t('login.forgotPassword')}
          </LinkButton>
        </Group>
      </Card>
    </>
  );
}
